import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import logger from '../lib/logger';

interface AdminRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

/**
 * AdminRoute - Endast för admin och owner roller
 * 
 * Funktioner:
 * - Kräver roll admin eller owner
 * - Teknician/agent/viewer blockeras
 * - Omdirigerar till /unauthorized vid otillräcklig behörighet
 */
const AdminRoute: React.FC<AdminRouteProps> = ({
  children,
  fallbackPath = '/unauthorized',
}) => {
  const { isAuthenticated, isLoading, role } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsChecking(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Loading state
  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Verifierar behörighet...
          </p>
        </div>
      </div>
    );
  }

  // Kontroll: Är användaren autentiserad?
  if (!isAuthenticated) {
    logger.audit('Oautentiserad användare blockerad från admin route', { 
      path: location.pathname 
    });
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Kontroll: Har användaren admin-roll?
  const adminRoles = ['admin', 'owner'];
  
  if (!role || !adminRoles.includes(role)) {
    logger.security('Användare saknar admin-behörighet', { 
      path: location.pathname,
      userRole: role,
    });
    
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ from: location.pathname, requiredRole: 'admin' }} 
        replace 
      />
    );
  }

  // Användaren har admin-behörighet
  logger.audit('Admin-behörighet godkänd', { 
    path: location.pathname,
    role,
  });

  return <>{children}</>;
};

export default AdminRoute;
